/**
 * ChatComposer — a lean, controlled chat input for EMBEDDED chat surfaces
 * (the Phase-3 copilot dock). Textarea with auto-size, Enter-to-send
 * (Shift+Enter for a newline) and a send/stop button.
 *
 * Deliberately NOT the standalone page's composer: that one carries
 * attachments, quote chips, prompt-history cycling, web/draft-mode toggles and
 * consent copy — host-page concerns that would bloat every embed. The full
 * composer stays in AgentPage; both call the same useChatTurn to actually send.
 */
import { useRef } from 'react';
import { FiArrowUp, FiSquare } from 'react-icons/fi';

const MAX_HEIGHT = 140;

export default function ChatComposer({
  value,
  onChange,
  onSend,
  onStop,
  loading = false,
  disabled = false,
  placeholder = 'Ask Vee…',
  autoFocus = false,
  onPaste,        // paste-to-attach passthrough (host handles clipboard files)
  leftAccessory,  // e.g. an attach button rendered inside the composer pill
}) {
  const ref = useRef(null);

  const autoSize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const natural = el.scrollHeight;
    el.style.height = `${Math.min(natural, MAX_HEIGHT)}px`;
    el.style.overflowY = natural > MAX_HEIGHT ? 'auto' : 'hidden';
  };

  const send = () => {
    if (disabled) return;
    const text = String(value || '').trim();
    if (!text) return;
    onSend(text);
    requestAnimationFrame(autoSize);
  };

  return (
    <div
      className="flex items-end gap-1.5 rounded-xl border bg-white px-2.5 py-1.5"
      style={{ borderColor: '#e2d9c8', boxShadow: '0 6px 18px -14px rgba(8,17,31,0.35)' }}
    >
      {leftAccessory}
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => { onChange(e.target.value); autoSize(); }}
        onPaste={onPaste}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
        }}
        rows={1}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
        className="flex-1 resize-none outline-none bg-transparent text-[13px] leading-relaxed text-[#08111f] py-1.5 px-1 placeholder:text-[#a09880]"
        style={{ maxHeight: MAX_HEIGHT }}
      />
      {loading && onStop ? (
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop generating"
          className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center text-white transition-transform active:scale-95"
          style={{ background: '#08111f' }}
        >
          <FiSquare size={12} />
        </button>
      ) : (
        <button
          type="button"
          onClick={send}
          disabled={disabled || !String(value || '').trim()}
          aria-label="Send message"
          className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center text-white transition-transform active:scale-95 disabled:opacity-30"
          style={{ background: '#145f5f' }}
        >
          <FiArrowUp size={14} />
        </button>
      )}
    </div>
  );
}
